import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Star, X } from "lucide-react";
import { toast } from "@/lib/toast";

/**
 * NPS prompt modal — auto-shows when a client has an installed project
 * >= 30 days old without an existing NPS response.
 * Polls /api/nps/me/pending once per mount.
 */
export default function NPSPrompt() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role !== "client") return;
    api.get("/nps/me/pending").then((r) => setPending(r.data.pending)).catch(() => {});
  }, [user?.role]);

  if (!pending) return null;

  const submit = async () => {
    if (score == null) { toast.error("Pick a score"); return; }
    setSubmitting(true);
    try {
      await api.post("/nps/submit", { score, project_id: pending.project_id, feedback });
      toast.success("Thanks for sharing!");
      setPending(null);
    } catch (e) { toast.error("Couldn't submit"); }
    finally { setSubmitting(false); }
  };

  const dismiss = async () => {
    try { await api.post("/nps/dismiss"); } catch {}
    setPending(null);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center px-4" role="dialog" aria-modal="true" aria-labelledby="nps-title" data-testid="nps-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A66] text-white p-6 relative">
          <button onClick={dismiss} aria-label="Dismiss" className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="nps-dismiss"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 mb-2 text-[#C9A356] text-xs uppercase tracking-widest"><Star className="w-3 h-3" /> Quick feedback</div>
          <h2 id="nps-title" className="font-heading text-2xl">How likely are you to recommend us?</h2>
          <p className="text-sm text-white/70 mt-1">Your project '{pending.project_title}' just hit 30 days. We'd love to hear from you.</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-11 gap-1 mb-3" role="radiogroup" aria-label="Score">
            {Array.from({ length: 11 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setScore(i)}
                data-testid={`nps-score-${i}`}
                role="radio"
                aria-checked={score === i}
                className={`aspect-square text-sm font-semibold rounded-md border-2 transition ${
                  score === i
                    ? "border-[#C9A356] bg-[#C9A356] text-white"
                    : "border-[#E2E8F0] dark:border-slate-700 text-[#475569] hover:border-[#C9A356]"
                }`}
              >{i}</button>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-[#94A3B8] mb-4 px-1">
            <span>Not at all likely</span><span>Extremely likely</span>
          </div>
          <textarea
            rows="3"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What stood out? (optional)"
            data-testid="nps-feedback"
            maxLength={1000}
            className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40"
          />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={dismiss} className="btn-outline-navy text-sm" data-testid="nps-later">Maybe later</button>
            <button onClick={submit} disabled={submitting || score == null} className="btn-accent text-sm" data-testid="nps-submit">{submitting ? "…" : "Send"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
